/**
 * Normalized integration catalog used by the agent integration hub.
 *
 * The hub never edits agent state directly; it computes a list of
 * `IntegrationTarget` entries from existing data sources and renders
 * them in a single searchable, filterable surface.
 *
 * BYOK (`bring your own LLM/model provider key`) is intentionally
 * excluded from this catalog. BYOK lives on the human user account
 * and is managed in billing/developer settings, not agent Tools.
 *
 * Provider ownership taxonomy (matches plan):
 *   personal_identity → human user account, identity/login only
 *   agent_oauth       → OAuth credential stored on the agent user id
 *   agent_byo_app     → user-supplied app credentials on the agent
 *   agent_api_key     → non-BYOK API key on the agent
 *   agent_mcp         → mcp:<name> credential on the agent
 *   agent_openapi     → openapi:<name> credential on the agent
 *   built_in_tool     → no external credential
 */

import { PROVIDERS, isProviderAvailable, type ProviderConfig } from '@/lib/auth/providers';
import {
  expandToolPolicyConfigMap,
  gatingHintsFor,
  suggestSafeDefaultScope,
  type ToolPolicyConfig,
  type ToolPolicyValue,
} from '@/lib/agents/tool-policies';
import type { HubMode } from './parse-hub-params';
import type { ToolPolicyItem } from './tool-policy-editor';

export type IntegrationKind =
  | 'platform'
  | 'messaging'
  | 'media_gen'
  | 'featured_mcp'
  | 'custom_mcp'
  | 'openapi'
  | 'custom_mcp_add'
  | 'custom_openapi_add';

export type IntegrationStatus =
  | 'connected'
  | 'available'
  | 'needs_account'
  | 'needs_tool_selection'
  | 'reconnect_required'
  | 'unsupported';

export type IntegrationOwnership =
  | 'personal_identity'
  | 'agent_oauth'
  | 'agent_byo_app'
  | 'agent_api_key'
  | 'agent_mcp'
  | 'agent_openapi';

export type IntegrationCategory =
  | 'all'
  | 'connected'
  | 'needs_setup'
  | 'messaging'
  | 'social'
  | 'productivity'
  | 'data_apis'
  | 'developer'
  | 'commerce'
  | 'media_gen'
  | 'mcp'
  | 'openapi';

export interface IntegrationTarget {
  /** Stable id used by deeplinks: `${kind}:${refId}`. */
  id: string;
  kind: IntegrationKind;
  /** Canonical brand id used by the deduped hub. */
  brandId: string;
  /** Provider id for platform/messaging/media_gen, server name for mcp/openapi. */
  refId: string;
  /** Modes available under this brand tile. */
  availableModes: HubMode[];
  /** Mode-specific refs used by the detail panel. */
  modeRefs?: Partial<Record<HubMode, string>>;
  /** Display name. */
  name: string;
  /** Lower-cased searchable description. */
  description?: string;
  iconSlug: string;
  status: IntegrationStatus;
  ownership: IntegrationOwnership;
  /** Optional categories used by the filter chips. */
  categories: IntegrationCategory[];
  /** Optional region note for catalog tiles. */
  regionNote?: string;
  /** Free-form search keywords. */
  keywords: string[];
  /** Number of tools currently exposed to the agent through this integration. */
  exposedToolCount: number;
  /** Number of tools available to potentially expose. */
  availableToolCount: number;
  tools?: ToolPolicyItem[];
  toolPolicies?: Record<string, ToolPolicyValue>;
  integrationRowId?: string;
  capabilityToolMap?: Record<string, string[]>;
  mcpConfig?: McpSkillEntry;
  openapiConfig?: OpenApiSkillEntry;
  metadata?: Record<string, unknown> | null;
  connectedAccount?: PlatformIntegrationRow['connectedAccount'];
}

// Database-shaped inputs ---------------------------------------------------

export interface PlatformIntegrationRow {
  id: string;
  type: 'platform' | 'custom_mcp';
  provider: string | null;
  name: string | null;
  enabledCapabilities: string[] | null;
  isEnabled: boolean | null;
  metadata?: Record<string, unknown> | null;
  mcpConfig?: Record<string, unknown> | null;
  mcpOauthConnected?: boolean;
  connectedAccountId?: string | null;
  connectedAccount?: {
    id: string;
    provider: string;
    providerUserId: string | null;
    providerUsername: string | null;
    authType: string | null;
    metadata?: Record<string, unknown> | null;
    needsReauth?: boolean | null;
  } | null;
}

export interface McpSkillEntry {
  enabled?: boolean;
  url?: string;
  authType?: string;
  authHeaderName?: string;
  transport?: 'sse' | 'http' | 'auto';
  mcpUrlTemplate?: string;
  enabledTools?: string[];
  toolExposureMode?: 'all' | 'whitelist' | 'blacklist';
  catalogId?: string | null;
  toolPolicies?: Record<string, ToolPolicyValue>;
  prompt?: { text?: string };
}

export interface OpenApiSkillEntry {
  enabled?: boolean;
  specUrl: string;
  baseUrl?: string;
  authType?: string;
  authHeaderName?: string;
  operations?: string[];
  operationExposureMode?: 'all' | 'whitelist' | 'blacklist';
  operationPolicies?: Record<string, 'allow' | 'notify' | 'block'>;
}

export interface FeaturedMcpEntry {
  id: string;
  brandId?: string;
  name: string;
  iconSlug: string;
  transport?: 'sse' | 'http' | 'auto';
  mcpUrl?: string;
  mcpUrlTemplate?: string;
  authType?: 'none' | 'api_key' | 'api_key_query' | 'oauth2';
  connectModes?: Array<{
    id: string;
    label: string;
    isDefault?: boolean;
    authType: 'api_key' | 'api_key_query' | 'oauth2';
    requiredCredential?: string;
    help?: string;
  }>;
  requiredCredential?: string;
  setupFields?: Array<{
    name: string;
    label: string;
    placeholder?: string;
    help?: string;
    kind?: 'identifier' | 'url';
    pattern?: string;
  }>;
  requiresExplicitTools?: boolean;
  requiresStaticClient?: boolean;
  regionNote?: string;
  prompt?: { name?: string; priority?: number; text: string };
}

export interface ConnectedAccountSummary {
  id: string;
  provider: string;
  /** True when this row's userId matches the agent user id, not the human. */
  isAgentOwned: boolean;
}

export interface BuildCatalogInput {
  agentIntegrations: PlatformIntegrationRow[];
  mcpSkills: Record<string, McpSkillEntry>;
  openapiSkills: Record<string, OpenApiSkillEntry>;
  featuredMcps: FeaturedMcpEntry[];
  /** Connected-account snapshot (any ownership). Used for agent-owned status. */
  connectedAccounts: ConnectedAccountSummary[];
  /** Provider ids the human user has personal-identity tokens for. */
  humanIdentityProviderIds: Set<string>;
}

const MEDIA_GEN_PROVIDERS = new Set(['fal', 'replicate']);

const MESSAGING_PROVIDERS = new Set([
  'whatsapp',
  'messenger',
  'instagram',
  'telegram',
  'twilio',
  'slack',
  'discord',
  'linkedin',
  'bluesky',
  'reddit',
  'google-chat',
  'sendgrid',
]);

const SOCIAL_PROVIDERS = new Set([
  'x',
  'linkedin',
  'reddit',
  'pinterest',
  'tiktok',
  'instagram',
  'bluesky',
  'patreon',
  // 'mastodon' removed: no PROVIDERS entry, no skill, no env wiring.
  // If/when Mastodon is added, restore the slug here AND add the
  // PROVIDERS row in lib/auth/providers.ts.
  'soundcloud',
  'twitch',
  'deviantart',
]);

const PRODUCTIVITY_PROVIDERS = new Set([
  'google',
  'notion',
  'asana',
  'calendly',
  'zoom',
  'microsoft',
  'figma',
  'dropbox',
]);

const DEVELOPER_PROVIDERS = new Set([
  'github',
  'supabase',
  'vercel',
  'cloudflare',
  'daytona',
  'n8n',
]);

const COMMERCE_PROVIDERS = new Set([
  'shopify',
  'stripe',
  'etsy',
  'eventbrite',
  'meetup',
  'ebay',
  'poshmark',
  'mercari',
  'ticketmaster',
  'uber',
]);

const MCP_PRIMARY_PROVIDERS = new Set([
  'asana',
]);

function classifyProviderCategories(providerId: string): IntegrationCategory[] {
  const cats: IntegrationCategory[] = [];
  if (MESSAGING_PROVIDERS.has(providerId)) cats.push('messaging');
  if (SOCIAL_PROVIDERS.has(providerId)) cats.push('social');
  if (PRODUCTIVITY_PROVIDERS.has(providerId)) cats.push('productivity');
  if (DEVELOPER_PROVIDERS.has(providerId)) cats.push('developer');
  if (COMMERCE_PROVIDERS.has(providerId)) cats.push('commerce');
  if (MEDIA_GEN_PROVIDERS.has(providerId)) cats.push('media_gen');
  if (cats.length === 0) cats.push('data_apis');
  return cats;
}

function brandIdForMcp(id: string, explicit?: string): string {
  return (explicit || id).replace(/-(admin|storefront)$/, '');
}

function providerMode(providerId: string, provider: ProviderConfig): HubMode {
  if (MEDIA_GEN_PROVIDERS.has(providerId) || provider.auth.type === 'api_key') return 'api_key';
  if (MESSAGING_PROVIDERS.has(providerId) || provider.auth.type === 'login_widget') return 'messaging';
  return 'oauth';
}

function sentenceFromPrompt(prompt?: { text?: string }): string | undefined {
  const text = prompt?.text?.trim();
  if (!text) return undefined;
  const [first] = text.split(/(?<=[.!?])\s+/);
  return first?.replace(/^Use\s+/i, '') || undefined;
}

function providerDescription(provider: ProviderConfig, capabilities = provider.capabilities): string {
  const labels = capabilities
    .slice(0, 2)
    .map((capability) => capability.label.replace(/\.$/, '').toLowerCase());
  if (labels.length === 0) return `Connect ${provider.name} to this agent.`;
  return labels.join('; ');
}

function upperFirst(text: string | undefined, fallback: string): string {
  const value = (text || fallback).trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : fallback;
}

function isDiscordWebhookRow(row: PlatformIntegrationRow | undefined): boolean {
  if (!row || row.provider !== 'discord') return false;
  const rowMeta = (row.metadata ?? {}) as Record<string, unknown>;
  const accountMeta = (row.connectedAccount?.metadata ?? {}) as Record<string, unknown>;
  return (
    rowMeta.credentialType === 'webhook' ||
    accountMeta.credentialType === 'webhook' ||
    ((row.enabledCapabilities ?? []).length === 1 && row.enabledCapabilities?.[0] === 'webhook_post')
  );
}

function providerCapabilitiesForRow(provider: ProviderConfig, row: PlatformIntegrationRow | undefined): ProviderConfig['capabilities'] {
  if (isDiscordWebhookRow(row)) {
    return provider.capabilities.filter((capability) => capability.id === 'webhook_post');
  }
  return provider.capabilities;
}

function providerToolItems(capabilities: ProviderConfig['capabilities']): ToolPolicyItem[] {
  return capabilities.flatMap((capability) =>
    capability.tools.map((tool) => ({
      name: tool,
      description: capability.description || capability.label,
      destructive: capability.modifies,
      gating: gatingHintsFor(tool),
    })),
  );
}

function policyFromRow(
  row: PlatformIntegrationRow | undefined,
  capabilities: ProviderConfig['capabilities'],
): Record<string, ToolPolicyConfig> {
  const stored = row?.metadata?.toolPolicies;
  const toolNames = capabilities.flatMap((capability) => capability.tools);
  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    return expandToolPolicyConfigMap(
      { toolPolicies: stored as Record<string, ToolPolicyValue> },
      toolNames,
    );
  }
  const enabledCaps = new Set(row?.enabledCapabilities ?? []);
  const out: Record<string, ToolPolicyConfig> = {};
  for (const cap of capabilities) {
    const policy = enabledCaps.size === 0 || enabledCaps.has(cap.id) ? 'allow' : 'block';
    for (const tool of cap.tools) out[tool] = { policy, scope: suggestSafeDefaultScope(tool) };
  }
  return out;
}

function ownershipForProvider(provider: ProviderConfig): IntegrationOwnership {
  switch (provider.auth.type) {
    case 'oauth2':
      return 'agent_oauth';
    case 'byo_app':
      return 'agent_byo_app';
    case 'api_key':
      return 'agent_api_key';
    case 'login_widget':
      // Telegram's login widget is used as an agent-side messaging connect
      // mode in the hub.
      return 'agent_oauth';
  }
}

function platformStatus(args: {
  row: PlatformIntegrationRow | undefined;
  hasAgentCredential: boolean;
  isMessaging: boolean;
}): IntegrationStatus {
  const { row, hasAgentCredential, isMessaging } = args;
  if (!row) {
    return hasAgentCredential ? 'available' : 'available';
  }
  if (row.isEnabled === false) return 'available';
  if (isMessaging && !row.connectedAccountId) {
    return hasAgentCredential ? 'needs_account' : 'needs_account';
  }
  if ((row.enabledCapabilities ?? []).length === 0) return 'needs_tool_selection';
  return 'connected';
}

function mcpStatus(args: {
  saved: McpSkillEntry | undefined;
  oauthConnected: boolean;
  authType?: string;
  requiresExplicitTools?: boolean;
}): IntegrationStatus {
  const { saved, oauthConnected, authType, requiresExplicitTools } = args;
  if (!saved) return 'available';
  if (saved.enabled === false) return 'available';
  if (authType === 'oauth2' && !oauthConnected) return 'reconnect_required';
  // Empty whitelist (`enabledTools: []`) means deny-all and demands user
  // action; treat as needs_tool_selection regardless of provider opt-in.
  if (Array.isArray(saved.enabledTools) && saved.enabledTools.length === 0) {
    return 'needs_tool_selection';
  }
  if (
    requiresExplicitTools &&
    (!saved.enabledTools || saved.enabledTools.length === 0)
  ) {
    return 'needs_tool_selection';
  }
  return 'connected';
}

function openapiStatus(saved: OpenApiSkillEntry): IntegrationStatus {
  if (saved.enabled === false) return 'available';
  if (saved.authType === 'api_key') {
    // The API key itself lives in connected_accounts as openapi:<name>;
    // the existence of the skill entry implies it was saved successfully.
    // Treat empty operations whitelist as needs_tool_selection.
    if (Array.isArray(saved.operations) && saved.operations.length === 0) {
      return 'needs_tool_selection';
    }
  }
  if (Array.isArray(saved.operations) && saved.operations.length === 0) {
    return 'needs_tool_selection';
  }
  return 'connected';
}

/**
 * Build a normalized catalog of integration targets for the hub.
 *
 * Stability:
 *   - Sorted by status (connected first), then category, then name, so
 *     deeplink/scroll behavior is deterministic.
 *   - Personal-identity-only providers are NOT promoted as "Connected"
 *     just because the human user has a personal identity token.
 */
export function buildIntegrationCatalog(input: BuildCatalogInput): IntegrationTarget[] {
  const brandTargets = new Map<string, IntegrationTarget>();
  const standaloneTargets: IntegrationTarget[] = [];

  const upsertBrand = (
    brandId: string,
    target: Omit<IntegrationTarget, 'id' | 'brandId' | 'availableModes' | 'modeRefs'> & {
      mode: HubMode;
      modeRef: string;
    },
  ) => {
    const existing = brandTargets.get(brandId);
    if (!existing) {
      brandTargets.set(brandId, {
        ...target,
        id: `brand:${brandId}`,
        brandId,
        availableModes: [target.mode],
        modeRefs: { [target.mode]: target.modeRef },
      });
      return;
    }

    if (!existing.availableModes.includes(target.mode)) {
      existing.availableModes.push(target.mode);
    }
    existing.modeRefs = { ...(existing.modeRefs ?? {}), [target.mode]: target.modeRef };
    existing.categories = Array.from(new Set([...existing.categories, ...target.categories]));
    existing.keywords = Array.from(new Set([...existing.keywords, ...target.keywords]));
    existing.exposedToolCount = Math.max(existing.exposedToolCount, target.exposedToolCount);
    existing.availableToolCount = Math.max(existing.availableToolCount, target.availableToolCount);
    existing.status =
      statusOrder(target.status) < statusOrder(existing.status) ? target.status : existing.status;
    if (existing.kind !== 'featured_mcp' && target.kind === 'featured_mcp') {
      existing.regionNote = target.regionNote ?? existing.regionNote;
    }
  };

  // Platform/messaging/media-gen entries built from PROVIDERS registry.
  for (const provider of Object.values(PROVIDERS)) {
    const id = provider.id;
    if (
      MCP_PRIMARY_PROVIDERS.has(id) &&
      input.featuredMcps.some((featured) => brandIdForMcp(featured.id, featured.brandId) === id)
    ) {
      continue;
    }
    const isMediaGen = MEDIA_GEN_PROVIDERS.has(id);
    const isMessaging = MESSAGING_PROVIDERS.has(id);
    const kind: IntegrationKind = isMediaGen
      ? 'media_gen'
      : isMessaging
        ? 'messaging'
        : 'platform';
    const ownership = ownershipForProvider(provider);
    const row = input.agentIntegrations.find(
      (r) => r.type === 'platform' && r.provider === id,
    );
    const hasAgentCredential = input.connectedAccounts.some(
      (a) => a.provider === id && a.isAgentOwned,
    );
    const includeProviderMode =
      provider.auth.type !== 'oauth2' ||
      isProviderAvailable(provider) ||
      !!row ||
      hasAgentCredential;
    if (!includeProviderMode) continue;
    const status = platformStatus({
      row,
      hasAgentCredential,
      isMessaging,
    });
    const cats = classifyProviderCategories(id);
    if (status === 'connected') cats.push('connected');
    if (status !== 'connected' && status !== 'available') cats.push('needs_setup');

    const effectiveCapabilities = providerCapabilitiesForRow(provider, row);
    const enabledCount = row?.enabledCapabilities?.length ?? 0;
    const totalTools = effectiveCapabilities.reduce(
      (sum, c) => sum + c.tools.length,
      0,
    );
    const exposedTools = (row?.enabledCapabilities ?? []).reduce((sum, capId) => {
      const cap = effectiveCapabilities.find((c) => c.id === capId);
      return sum + (cap?.tools.length ?? 0);
    }, 0);

    const mode = providerMode(id, provider);
    upsertBrand(id, {
      kind,
      refId: id,
      mode,
      modeRef: id,
      name: provider.name,
      iconSlug: provider.simpleIconSlug,
      description: upperFirst(providerDescription(provider, effectiveCapabilities), `Connect ${provider.name}.`),
      status,
      ownership,
      categories: cats,
      keywords: [
        provider.name.toLowerCase(),
        provider.id.toLowerCase(),
        provider.category,
        ...effectiveCapabilities.map((c) => c.label.toLowerCase()),
        ownership.replace('_', ' '),
        ...(enabledCount > 0 ? ['connected'] : []),
      ],
      exposedToolCount: exposedTools,
      availableToolCount: totalTools,
      tools: providerToolItems(effectiveCapabilities),
      toolPolicies: policyFromRow(row, effectiveCapabilities),
      integrationRowId: row?.id,
      capabilityToolMap: Object.fromEntries(effectiveCapabilities.map((cap) => [cap.id, cap.tools])),
      metadata: row?.metadata,
      connectedAccount: row?.connectedAccount ?? null,
    });
  }

  // Featured MCP entries.
  for (const featured of input.featuredMcps) {
    const saved = input.mcpSkills[featured.id];
    const oauthConnected = input.connectedAccounts.some(
      (a) => a.provider === `mcp:${featured.id}` && a.isAgentOwned,
    );
    const status = mcpStatus({
      saved,
      oauthConnected,
      authType: featured.authType,
      requiresExplicitTools: featured.requiresExplicitTools,
    });
    const cats: IntegrationCategory[] = ['mcp'];
    if (status === 'connected') cats.push('connected');
    else if (status !== 'available') cats.push('needs_setup');
    const exposed = saved?.enabledTools?.length ?? 0;
    const brandId = brandIdForMcp(featured.id, featured.brandId);
    upsertBrand(brandId, {
      kind: 'featured_mcp',
      refId: featured.id,
      mode: 'mcp_hosted',
      modeRef: featured.id,
      name: featured.name,
      iconSlug: featured.iconSlug,
      description: upperFirst(sentenceFromPrompt(featured.prompt), `${featured.name} MCP tools.`),
      regionNote: featured.regionNote,
      status,
      ownership: 'agent_mcp',
      categories: cats,
      keywords: [featured.name.toLowerCase(), featured.id.toLowerCase(), 'mcp'],
      exposedToolCount: exposed,
      availableToolCount: exposed, // discovered tools only known after probe
      toolPolicies: saved?.toolPolicies,
      mcpConfig: saved,
    });
  }

  // Custom MCP entries (saved but not in featured catalog).
  for (const [name, saved] of Object.entries(input.mcpSkills)) {
    if (input.featuredMcps.some((f) => f.id === name)) continue;
    const oauthConnected = input.connectedAccounts.some(
      (a) => a.provider === `mcp:${name}` && a.isAgentOwned,
    );
    const status = mcpStatus({
      saved,
      oauthConnected,
      authType: saved.authType,
    });
    const cats: IntegrationCategory[] = ['mcp'];
    if (status === 'connected') cats.push('connected');
    else if (status !== 'available') cats.push('needs_setup');
    const exposed = saved.enabledTools?.length ?? 0;
    standaloneTargets.push({
      id: `custom_mcp:${name}`,
      kind: 'custom_mcp',
      brandId: name,
      refId: name,
      availableModes: ['mcp_hosted'],
      modeRefs: { mcp_hosted: name },
      name,
      iconSlug: 'mcp',
      description: 'Custom MCP tools.',
      status,
      ownership: 'agent_mcp',
      categories: cats,
      keywords: [name.toLowerCase(), 'mcp', 'custom'],
      exposedToolCount: exposed,
      availableToolCount: exposed,
      toolPolicies: saved.toolPolicies,
      mcpConfig: saved,
    });
  }

  // OpenAPI entries.
  for (const [name, saved] of Object.entries(input.openapiSkills)) {
    const status = openapiStatus(saved);
    const cats: IntegrationCategory[] = ['openapi'];
    if (status === 'connected') cats.push('connected');
    else if (status !== 'available') cats.push('needs_setup');
    const exposed = saved.operations?.length ?? 0;
    standaloneTargets.push({
      id: `openapi:${name}`,
      kind: 'openapi',
      brandId: 'custom_openapi',
      refId: name,
      availableModes: ['openapi'],
      modeRefs: { openapi: name },
      name,
      iconSlug: 'openapi',
      description: 'Custom API actions.',
      status,
      ownership: 'agent_openapi',
      categories: cats,
      keywords: [name.toLowerCase(), 'openapi', 'api'],
      exposedToolCount: exposed,
      availableToolCount: exposed,
      openapiConfig: saved,
      toolPolicies: saved.operationPolicies,
    });
  }

  standaloneTargets.push(
    {
      id: 'custom_mcp_add:new',
      kind: 'custom_mcp_add',
      brandId: 'custom_mcp',
      refId: 'new',
      availableModes: ['mcp_hosted'],
      modeRefs: { mcp_hosted: 'new' },
      name: 'Add MCP server',
      iconSlug: 'mcp',
      description: 'Connect a custom MCP endpoint.',
      status: 'available',
      ownership: 'agent_mcp',
      categories: ['mcp'],
      keywords: ['custom', 'mcp', 'server'],
      exposedToolCount: 0,
      availableToolCount: 0,
    },
    {
      id: 'custom_openapi_add:new',
      kind: 'custom_openapi_add',
      brandId: 'custom_openapi',
      refId: 'new',
      availableModes: ['openapi'],
      modeRefs: { openapi: 'new' },
      name: 'Add OpenAPI server',
      iconSlug: 'openapi',
      description: 'Connect a custom OpenAPI spec.',
      status: 'available',
      ownership: 'agent_openapi',
      categories: ['openapi'],
      keywords: ['custom', 'openapi', 'api'],
      exposedToolCount: 0,
      availableToolCount: 0,
    },
  );

  return sortCatalog([...brandTargets.values(), ...standaloneTargets]);
}

function statusOrder(status: IntegrationStatus): number {
  switch (status) {
    case 'connected':
      return 0;
    case 'needs_tool_selection':
      return 1;
    case 'reconnect_required':
      return 2;
    case 'needs_account':
      return 3;
    case 'available':
      return 4;
    case 'unsupported':
      return 5;
  }
}

function sortCatalog(targets: IntegrationTarget[]): IntegrationTarget[] {
  return [...targets].sort((a, b) => {
    const ac = a.kind === 'custom_mcp_add' || a.kind === 'custom_openapi_add';
    const bc = b.kind === 'custom_mcp_add' || b.kind === 'custom_openapi_add';
    if (ac !== bc) return ac ? -1 : 1;
    if (ac && bc) {
      const addOrder = ['custom_mcp_add', 'custom_openapi_add'];
      return addOrder.indexOf(a.kind) - addOrder.indexOf(b.kind);
    }
    const as = a.kind === 'custom_mcp' || a.kind === 'openapi';
    const bs = b.kind === 'custom_mcp' || b.kind === 'openapi';
    if (as !== bs) return as ? -1 : 1;
    const ds = statusOrder(a.status) - statusOrder(b.status);
    if (ds !== 0) return ds;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Filter the catalog by category + free-text query.
 *
 * Special categories:
 *   - 'all'         → no filter
 *   - 'connected'   → status === 'connected'
 *   - 'needs_setup' → any status that requires user action
 */
export function filterCatalog(
  targets: IntegrationTarget[],
  category: IntegrationCategory,
  query: string,
): IntegrationTarget[] {
  const q = query.trim().toLowerCase();
  return targets.filter((t) => {
    if (category === 'connected' && t.status !== 'connected') return false;
    if (category === 'needs_setup') {
      if (t.status === 'connected' || t.status === 'available') return false;
    }
    if (
      category !== 'all' &&
      category !== 'connected' &&
      category !== 'needs_setup' &&
      !t.categories.includes(category)
    ) {
      return false;
    }
    if (q) {
      if (t.kind === 'custom_mcp_add' || t.kind === 'custom_openapi_add') return false;
      if (
        !t.name.toLowerCase().includes(q) &&
        !t.keywords.some((k) => k.includes(q))
      ) {
        return false;
      }
    }
    return true;
  });
}

/** Find a target by stable id (`${kind}:${refId}`). */
export function findTargetById(
  targets: IntegrationTarget[],
  id: string,
): IntegrationTarget | undefined {
  return targets.find((t) => t.id === id);
}

/** Resolve a deep-link descriptor to a catalog target id. */
export function resolveDeeplinkTarget(args: {
  targets: IntegrationTarget[];
  mcpConnect?: string | null;
  openapiConnect?: string | null;
  integrationType?: string | null;
  integrationProvider?: string | null;
}): IntegrationTarget | null {
  const { targets, mcpConnect, openapiConnect, integrationType, integrationProvider } = args;
  if (mcpConnect) {
    const featured = targets.find(
      (t) =>
        t.availableModes.includes('mcp_hosted') &&
        (t.refId === mcpConnect || t.modeRefs?.mcp_hosted === mcpConnect || t.brandId === mcpConnect),
    );
    if (featured) return featured;
    const custom = targets.find(
      (t) => t.kind === 'custom_mcp' && t.refId === mcpConnect,
    );
    if (custom) return custom;
  }
  if (openapiConnect) {
    const o = targets.find((t) => t.kind === 'openapi' && t.refId === openapiConnect);
    if (o) return o;
  }
  if (integrationType && integrationProvider) {
    const kindMap: Record<string, IntegrationKind> = {
      platform: 'platform',
      messaging: 'messaging',
      media_gen: 'media_gen',
    };
    const kind = kindMap[integrationType];
    if (kind) {
      const target = targets.find(
        (t) => t.brandId === integrationProvider || (t.kind === kind && t.refId === integrationProvider),
      );
      if (target) return target;
      // Allow loose matching across kinds (the same provider id may
      // be classified as messaging vs platform depending on heuristics).
      const fallback = targets.find((t) => t.refId === integrationProvider);
      if (fallback) return fallback;
    }
  }
  return null;
}
